import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getOrganisationMaxLevel } from '@/api/core/organisation';
import { getOrganisationSharings } from '@/api/core/organisationSharing';
import { log } from '@/utils/vueLogger.js';
import { buildDeliveryLocationAttributeMaps, resolveDeliveryLocationAttributes } from '@/services/deliveryLocationService';
import { useTranslator } from '@/utils/useTranslator';
import { getLatestAppointmentsWithStatements } from '@/api/sales/appointment';
import { getProductTreeRestrictions } from '@/api/core/restriction';
const t = useTranslator();

const enumKPI = {
	kpiToSum: ['TotalCode', 'TotalPicking', 'TotalMissingProduct', 'TotalOOSProduct', 'PromoNbToNego', 'PromoVolumeToNego', 'PromoNbToState', 'PromoVolumeToState'], // list of KPIs that must be calculated with a sum when grouped
	kpiToAvg: ['VisitRate', 'ImplantationQuality', 'DetentionRate', 'ShareOfShelf', 'OOSRate', 'NumericDistribution'], // list of KPIs that must be calculated with an avg when grouped
	valueIsPercentage: ['ImplantationQuality', 'DetentionRate', 'ShareOfShelf', 'OOSRate', 'NumericDistribution'], // KPIs representing a %, others are just a number(value)
	kpiToExcludeForVisit: ['VisitRate', 'PromoNbToNego', 'PromoVolumeToNego', 'PromoNbToState', 'PromoVolumeToState'],
	kpiByCluster: ['PromoNbToNego', 'PromoVolumeToNego', 'PromoNbToState', 'PromoVolumeToState'],
	kpiBuilder: ['DetentionRate', 'ShareOfShelf', 'OOSRate', 'NumericDistribution', 'TotalCode', 'TotalPicking', 'TotalMissingProduct', 'TotalOOSProduct'],
};

const KPI_COMPUTE_MAX_CONCURRENCY = 4;
let _kpiComputeActive = 0;
const _kpiComputeQueue = [];

function _withKpiConcurrencyLimit(task) {
	return new Promise((resolve, reject) => {
		const run = () => {
			_kpiComputeActive++;
			Promise.resolve()
				.then(task)
				.then(resolve, reject)
				.finally(() => {
					_kpiComputeActive--;
					const next = _kpiComputeQueue.shift();
					if (next) next();
				});
		};
		if (_kpiComputeActive < KPI_COMPUTE_MAX_CONCURRENCY) run();
		else _kpiComputeQueue.push(run);
	});
}

class KpiService {
	_normalizeFilters(filters = {}) {
		const deliveryLocationTypes = filters.deliveryLocationTypes ?? filters.dlts ?? null;
		const productTrees = filters.productTrees ?? filters.pts ?? null;
		const specificities = filters.specificities ?? filters.spes ?? null;

		return {
			...filters,
			deliveryLocationTypes,
			dlts: deliveryLocationTypes,
			productTrees,
			pts: productTrees,
			specificities,
			spes: specificities,
			organisations: filters.organisations ?? null,
			companies: filters.companies ?? null,
			simulatedMode: filters.simulatedMode ?? filters.simulated ?? false,
			allPark: filters.allPark ?? false,
			conditionProduct: filters.conditionProduct ?? null,
			conditionDeliveryLocation: filters.conditionDeliveryLocation ?? null,
			conditionShelfStatement: filters.conditionShelfStatement ?? null,
			deliveryLocationIds: filters.deliveryLocationIds ?? null,
			localAppointments: filters.localAppointments ?? null,
		};
	}

	_getAppointmentUid(appointment) {
		if (!appointment) return null;
		if (appointment.uid) return appointment.uid;
		const statements = appointment._statements || appointment.statements;
		if (Array.isArray(statements) && statements.length > 0) {
			return statements[0].appointmentUid || null;
		}
		return null;
	}

	_getEcosCollection(collection) {
		if (typeof collection === 'function') {
			return collection() || [];
		}

		return Array.isArray(collection) ? collection : [];
	}

	_getKpiDefinition(kpiReference) {
		const ecosVM = iframeDataBridge.getEcosVM();
		let kpis = this._getEcosCollection(ecosVM?.kpis);
		kpis = [...kpis, ...this._getEcosCollection(ecosVM?.kpisCustom)];
		return kpis.find((kpi) => kpi.reference && kpi.reference.toLowerCase() === kpiReference.toLowerCase()) || null;
	}

	_parseCustomKpiFilters(filters) {
		if (!filters) {
			return {};
		}

		if (typeof filters === 'string') {
			try {
				const parsedFilters = JSON.parse(filters);
				return parsedFilters && typeof parsedFilters === 'object' && !Array.isArray(parsedFilters) ? parsedFilters : {};
			} catch {
				return {};
			}
		}

		return typeof filters === 'object' && !Array.isArray(filters) ? filters : {};
	}

	_kpiDtoConditionToFcb(c) {
		const inv = !!c.inverted;
		let op;
		if (c.operator === 1)
			op = inv ? 12 : 11; // Empty / NotEmpty
		else if (c.operator === 2)
			op = inv ? 2 : 1; // Equal / NotEqual
		else if (c.operator === 3)
			op = inv ? 10 : 7; // Greater / LowerOrEqual
		else if (c.operator === 4)
			op = inv ? 9 : 8; // Lower / GreaterOrEqual
		else if (c.operator === 5)
			op = 17; // InList
		else op = 1;
		return {
			attributeId: c.attributeId,
			localizationKey: c.propertyName || '',
			propertyName: c.propertyName || '',
			operator: op,
			value: c.value || '',
			logicalOperator: c.linkType === 0 ? 2 : 1,
		};
	}

	_remapKpiConditionFields(filters) {
		if (!filters || typeof filters !== 'object') return filters ?? {};
		const result = { ...filters };
		if (result.productConditions?.length && !result.conditionProduct?.length) result.conditionProduct = result.productConditions.map((c) => this._kpiDtoConditionToFcb(c));
		if (result.deliveryLocationConditions?.length && !result.conditionDeliveryLocation?.length) result.conditionDeliveryLocation = result.deliveryLocationConditions.map((c) => this._kpiDtoConditionToFcb(c));
		if (result.shelfStatementConditions?.length && !result.conditionShelfStatement?.length) result.conditionShelfStatement = result.shelfStatementConditions.map((c) => this._kpiDtoConditionToFcb(c));
		return result;
	}

	_mergeCustomFilterValues(sourceValues, customValues) {
		const normalizedSourceValues = Array.isArray(sourceValues) ? [...new Set(sourceValues.filter((value) => value !== null && value !== undefined))] : [];
		const normalizedCustomValues = Array.isArray(customValues) ? [...new Set(customValues.filter((value) => value !== null && value !== undefined))] : [];

		if (!normalizedCustomValues.length) {
			return normalizedSourceValues;
		}

		if (!normalizedSourceValues.length) {
			return normalizedCustomValues;
		}

		return normalizedSourceValues.filter((value) => normalizedCustomValues.includes(value));
	}

	_mergeFiltersWithCustomKpi(filters = {}, customFilters = {}) {
		const normalizedFilters = this._normalizeFilters(filters);
		const normalizedCustomFilters = this._normalizeFilters(customFilters);
		const mergedFilters = { ...normalizedFilters };
		const filterKeys = ['accounts', 'channels', 'deliveryLocationTypes', 'productTrees', 'specificities', 'organisations', 'companies', 'clusters'];

		filterKeys.forEach((filterKey) => {
			mergedFilters[filterKey] = this._mergeCustomFilterValues(normalizedFilters[filterKey], normalizedCustomFilters[filterKey]);
		});

		if ((mergedFilters.clusterId === null || mergedFilters.clusterId === undefined) && normalizedCustomFilters.clusterId !== null && normalizedCustomFilters.clusterId !== undefined) {
			mergedFilters.clusterId = normalizedCustomFilters.clusterId;
		}

		const conditionFields = ['conditionProduct', 'conditionDeliveryLocation', 'conditionShelfStatement'];
		conditionFields.forEach((key) => {
			if (normalizedCustomFilters[key] != null) mergedFilters[key] = normalizedCustomFilters[key];
		});

		return this._normalizeFilters(mergedFilters);
	}

	_resolveKpiComputationContext(kpiReference, filters = {}) {
		let kpi = this._getKpiDefinition(kpiReference);

		if (!kpi) {
			return null;
		}

		if (!kpi.kpiId) {
			return {
				kpi,
				computationKpi: kpi,
				computationReference: kpi.reference,
				normalizedFilters: this._normalizeFilters(filters),
			};
		}

		const ecosVM = iframeDataBridge.getEcosVM();
		const baseKpi = this._getEcosCollection(ecosVM?.kpis).find((mainKpi) => Number(mainKpi?.id) === Number(kpi.kpiId));
		const computationKpi = baseKpi
			? {
					...baseKpi,
					tolerance: kpi.tolerance ?? baseKpi.tolerance,
					icon: kpi.icon || baseKpi.icon,
				}
			: kpi;
		const customKpiFilters = this._remapKpiConditionFields(this._parseCustomKpiFilters(kpi.filters ?? kpi.filter));

		const mergedFilters = this._mergeFiltersWithCustomKpi(filters, customKpiFilters);

		// A custom KPI is cluster-based when at least one of its clustering objectives has no
		// deliveryLocationConditions — meaning it matches by clusteringId, not by DL attributes.
		// Attribute-only KPIs (all objectives have conditions) must NOT exclude cluster-less DLs.
		const isClusterBasedKpi = (kpi.clusterings || []).some((cluster) => (cluster.objectives || []).some((obj) => (obj.filters?.deliveryLocationConditions || []).length === 0));
		if (isClusterBasedKpi) mergedFilters.requiresCluster = true;

		return {
			kpi,
			computationKpi,
			computationReference: computationKpi.reference || kpi.reference,
			normalizedFilters: mergedFilters,
		};
	}

	/**
	 * Retrieves KPI data based on its reference
	 * @param {string} kpiReference - KPI reference (e.g. 'detentionRate')
	 * @param {Object} filters - Filters to apply (deliveryLocationTypes, accounts, channels, pts, spes)
	 * @returns {Promise<Object>} Formatted KPI data
	 */
	async getKpiData(kpiReference, filters = {}) {
		return _withKpiConcurrencyLimit(() => this._computeKpiData(kpiReference, filters));
	}

	async _computeKpiData(kpiReference, filters = {}) {
		try {
			log('info', 'KpiService', `Getting KPI data for reference: ${kpiReference}`);
			const kpiContext = this._resolveKpiComputationContext(kpiReference, filters);

			if (!kpiContext) {
				log('warn', 'KpiService', `KPI with reference '${kpiReference}' not found.`);
				return null;
			}

			const { kpi, computationKpi, computationReference } = kpiContext;
			let { normalizedFilters } = kpiContext;

			// Access legacy data through iframe bridge
			const ecosVM = iframeDataBridge.getEcosVM();

			if (!ecosVM || !ecosVM.kpis) {
				throw new Error('EcosVM or KPIs not available');
			}

			// Apply product tree read restrictions for the current user's role
			const allowedProductTreeIds = await this._resolveAllowedProductTreeIds();
			normalizedFilters = this._applyProductTreeRestrictions(normalizedFilters, allowedProductTreeIds);

			// Get filtered delivery locations
			const deliveryLocations = this._getFilteredDeliveryLocations(normalizedFilters);

			if (deliveryLocations.length === 0) {
				log('warn', 'KpiService', 'No delivery locations found after filtering');
				return {
					value: 0,
					formattedValue: this.isPercentage(computationReference) ? '0 %' : '0',
					percentage: 0,
					difference: '0',
					formattedDifference: this.isPercentage(computationReference) ? '0 %' : '0',
					classColor: '',
					color: 'primary',
					icon: normalizedFilters.icon || kpi.icon || '',
					objective: null,
					hasRealObjective: false,
					kpi: kpi,
				};
			}

			// Get KPI data
			const kpiData = await this._calculateKpiData(computationKpi, deliveryLocations, normalizedFilters, kpi);

			return {
				value: kpiData.value,
				formattedValue: this.isPercentage(computationReference) ? `${kpiData.value} %` : `${kpiData.value}`,
				percentage: kpiData.percentage,
				difference: kpiData.difference,
				formattedDifference: this.isPercentage(computationReference) ? `${kpiData.difference} %` : kpiData.difference,
				classColor: kpiData.classColor,
				color: kpiData.color,
				icon: normalizedFilters.icon || kpi.icon || '',
				objective: kpiData.objective,
				hasRealObjective: kpiData.hasRealObjective ?? false,
				kpi: kpi,
			};
		} catch (error) {
			log('error', 'KpiService', `Error retrieving KPI data: ${error.message}`);
			throw error;
		}
	}

	async getKpiDetailRows(kpiReference, filters = {}) {
		try {
			log('info', 'KpiService', `Getting KPI detail rows for reference: ${kpiReference}`);
			const kpiContext = this._resolveKpiComputationContext(kpiReference, filters);

			if (!kpiContext) {
				log('warn', 'KpiService', `KPI with reference '${kpiReference}' not found for detail rows.`);
				return [];
			}

			const { computationKpi, kpi } = kpiContext;
			let { normalizedFilters } = kpiContext;

			// Apply product tree read restrictions for the current user's role
			const allowedProductTreeIds = await this._resolveAllowedProductTreeIds();
			normalizedFilters = this._applyProductTreeRestrictions(normalizedFilters, allowedProductTreeIds);

			const deliveryLocations = this._getFilteredDeliveryLocations(normalizedFilters);
			if (deliveryLocations.length === 0) {
				return [];
			}

			const deliveryLocationIds = deliveryLocations.map((deliveryLocation) => deliveryLocation.id);
			const rawAppointments = await this._getLatestAppointments(deliveryLocationIds, 1, normalizedFilters.localAppointments);

			const { deliveryLocations: companyFilteredDeliveryLocations, latestAppointments } = this._filterByCompanies(deliveryLocations, rawAppointments, normalizedFilters.companies);
			const visitedDlIds = new Set(latestAppointments.map((a) => a.deliveryLocationId));
			const filteredDeliveryLocations = normalizedFilters.allPark ? companyFilteredDeliveryLocations : companyFilteredDeliveryLocations.filter((dl) => visitedDlIds.has(dl.id));
			const filteredDeliveryLocationIds = filteredDeliveryLocations.map((dl) => dl.id);

			if (computationKpi.reference === 'ShareOfShelf') {
				normalizedFilters.simulatedMode = true;
			}

			const { appointments: computationAppointments, activeDlIds } = await this._injectFakeAppointments(latestAppointments, filteredDeliveryLocationIds, computationKpi.reference, normalizedFilters.simulatedMode ?? false);

			return this._buildKpiDetailRows(computationKpi, filteredDeliveryLocations, computationAppointments, activeDlIds, normalizedFilters, kpi);
		} catch (error) {
			log('error', 'KpiService', `Error retrieving KPI detail rows: ${error.message}`);
			throw error;
		}
	}

	/**
	 * Filters delivery locations and appointments by appointment.companies.
	 * - No filter: returns unchanged
	 * - Filter active: keeps DLs whose last appointment has no companies or has a matching companyId.
	 *   DLs with no appointment are treated as "no companies" and are always kept.
	 * @private
	 */
	_filterByCompanies(deliveryLocations, latestAppointments, companiesFilter) {
		if (!Array.isArray(companiesFilter) || companiesFilter.length === 0) {
			return { deliveryLocations, latestAppointments };
		}

		const selectedCompanyIds = new Set(companiesFilter.map((id) => Number(id)));
		const appointmentByDlId = new Map(latestAppointments.map((a) => [a.deliveryLocationId, a]));

		const filteredDeliveryLocations = deliveryLocations.filter((dl) => {
			const appointment = appointmentByDlId.get(dl.id);
			if (!appointment) return true;
			const companies = Array.isArray(appointment.companies) ? appointment.companies : [];
			if (companies.length === 0) return true;
			return companies.some((c) => selectedCompanyIds.has(Number(c.companyId)));
		});

		const filteredDlIdSet = new Set(filteredDeliveryLocations.map((dl) => dl.id));
		const filteredAppointments = latestAppointments.filter((a) => filteredDlIdSet.has(a.deliveryLocationId));

		return { deliveryLocations: filteredDeliveryLocations, latestAppointments: filteredAppointments };
	}

	_expandOrganisationIdsWithAncestors(organisationIds, organisationById) {
		const expanded = new Set();
		for (const orgId of organisationIds) {
			const numericId = Number(orgId);
			if (!Number.isFinite(numericId) || numericId <= 0) continue;
			expanded.add(numericId);
			let currentOrg = organisationById.get(numericId);
			while (currentOrg?.parentId) {
				const parentId = Number(currentOrg.parentId);
				if (!Number.isFinite(parentId) || parentId <= 0) break;
				expanded.add(parentId);
				currentOrg = organisationById.get(parentId);
			}
		}
		return expanded;
	}

	_isAncestorOf(candidateId, org, organisationById) {
		const visited = new Set();
		let current = org;
		while (current?.parentId) {
			const parentId = Number(current.parentId);
			if (!Number.isFinite(parentId) || parentId <= 0 || visited.has(parentId)) break;
			visited.add(parentId);
			if (parentId === candidateId) return true;
			current = organisationById.get(parentId);
		}
		return false;
	}

	_buildOrganisationPaths(organisations, organisationById, organisationMaxLevel) {
		if (!organisations || organisations.length === 0) {
			return [{}];
		}

		// Keep only leaf orgs: those not being an ancestor of any other org in the list
		const leafOrgs = organisations.filter((org) => {
			return !organisations.some((other) => Number(other.id) !== Number(org.id) && this._isAncestorOf(Number(org.id), other, organisationById));
		});

		const targetOrgs = leafOrgs.length > 0 ? leafOrgs : [organisations[0]];

		return targetOrgs.map((org) => {
			const levels = {};
			const maxLvl = Math.min(org.level, organisationMaxLevel);
			levels[`organisationLevel${maxLvl}`] = org.name || org.label || org.reference || '';
			let current = org;
			for (let level = maxLvl - 1; level >= 1; level--) {
				if (!current?.parentId) break;
				const parent = organisationById.get(Number(current.parentId));
				levels[`organisationLevel${level}`] = parent ? parent.name || parent.label || parent.reference || '' : '';
				current = parent;
			}
			return levels;
		});
	}

	_buildUserVisibleOrgIds(userOrgId, organisationById) {
		if (!userOrgId || userOrgId <= 0) return null;
		const userOrg = organisationById.get(userOrgId);
		if (!userOrg) return null;

		const visible = new Set([userOrgId]);
		for (const [id, org] of organisationById) {
			if (this._isAncestorOf(userOrgId, org, organisationById)) visible.add(Number(id));
		}
		let current = organisationById.get(userOrgId);
		while (current?.parentId) {
			visible.add(Number(current.parentId));
			current = organisationById.get(Number(current.parentId));
		}
		return visible;
	}

	_buildOrgSharingExpansionMap(sharings) {
		const map = new Map();
		for (const sharing of sharings || []) {
			const parentId = Number(sharing.parentOrganisationId);
			const childIds = (sharing.organisations || []).map(Number);
			const poolMembers = [parentId, ...childIds].filter((id) => Number.isFinite(id) && id > 0);
			for (const memberId of poolMembers) {
				if (!map.has(memberId)) map.set(memberId, new Set());
				for (const otherId of poolMembers) {
					if (otherId !== memberId) map.get(memberId).add(otherId);
				}
			}
		}
		return map;
	}

	/**
	 * Builds a Map<deliveryLocationId, attributes[]> from ecosVM.deliveryLocationsAttr.
	 * Custom delivery-location attributes (e.g. ParcAdditionnel) are NOT stored on the
	 * deliveryLocations objects — the legacy app keeps them in a separate collection keyed by DL id
	 * (see pos.js __dlSaved / eagle.js getDeliveryLocationAttributes). Attribute-based KPI conditions
	 * therefore have to source the value from here, or _evalCondition resolves it to undefined.
	 * @private
	 */
	_getDeliveryLocationAttributesById() {
		const ecosVM = iframeDataBridge.getEcosVM();
		const entries = this._getEcosCollection(ecosVM?.deliveryLocationsAttr);
		const map = new Map();
		entries.forEach((entry) => {
			const deliveryLocationId = Number(entry?.id ?? entry?.deliveryLocationId);
			if (Number.isFinite(deliveryLocationId) && Array.isArray(entry?.attributes)) {
				map.set(deliveryLocationId, entry.attributes);
			}
		});
		return map;
	}

	/**
	 * Filters delivery locations based on criteria
	 * @private
	 */
	_getFilteredDeliveryLocations(filters) {
		const normalizedFilters = this._normalizeFilters(filters);
		const ecosVM = iframeDataBridge.getEcosVM();
		const context = iframeDataBridge.getContext();
		const organisationById = new Map(this._getEcosCollection(ecosVM?.organisations).map((org) => [Number(org.id), org]));

		// Use own delivery locations or all depending on context
		const rawDeliveryLocations = context.filterByOwnDeliveryLocations ? ecosVM.ownDeliveryLocations : ecosVM.deliveryLocations;
		const allDeliveryLocations = (rawDeliveryLocations || []).filter((dl) => dl.accountId > 0);

		if (allDeliveryLocations.length === 0) {
			return [];
		}

		// If no filters specified, return all
		if (!normalizedFilters.accounts && !normalizedFilters.channels && !normalizedFilters.deliveryLocationTypes && !normalizedFilters.organisations && !normalizedFilters.deliveryLocationIds?.length && !normalizedFilters.conditionDeliveryLocation?.length && !normalizedFilters.clusters?.length) {
			return allDeliveryLocations;
		}

		// Custom-KPI DL conditions can target custom attributes (ParcAdditionnel, etc.). Those live in a
		// separate collection, not on the dl objects, so resolve them here — otherwise a NotEquals attribute
		// condition passes for every store (undefined !== value) and the excluded stores stay in the list.
		const hasAttributeCondition = Array.isArray(normalizedFilters.conditionDeliveryLocation) && normalizedFilters.conditionDeliveryLocation.some((condition) => Number(condition?.attributeId) > 0);
		const deliveryLocationAttributesById = hasAttributeCondition ? this._getDeliveryLocationAttributesById() : null;

		return allDeliveryLocations.filter((dl) => {
			// Filter by delivery location type
			if (normalizedFilters.deliveryLocationTypes && normalizedFilters.deliveryLocationTypes.length > 0) {
				if (!normalizedFilters.deliveryLocationTypes.includes(dl.deliveryLocationTypeId)) {
					return false;
				}
			}

			// Filter by account
			if (normalizedFilters.accounts && normalizedFilters.accounts.length > 0) {
				if (!normalizedFilters.accounts.includes(dl.accountId)) {
					return false;
				}
			}

			// Filter by channel
			if (normalizedFilters.channels && normalizedFilters.channels.length > 0) {
				if (!normalizedFilters.channels.includes(dl.channelId)) {
					return false;
				}
			}

			// Filter by organisation — also accept stores whose org is an ancestor of a selected org
			// (e.g. a store assigned to "Paris" is visible when filtering by its child org "CS1")
			if (normalizedFilters.organisations && normalizedFilters.organisations.length > 0) {
				const deliveryLocationOrganisations = (Array.isArray(dl.organisations) ? dl.organisations : []).map(Number);
				const expandedOrgIds = this._expandOrganisationIdsWithAncestors(normalizedFilters.organisations, organisationById);
				if (!deliveryLocationOrganisations.some((organisationId) => expandedOrgIds.has(organisationId))) {
					return false;
				}
			}

			// Filter by list of allowed clusters (from custom KPI filters.clusters)
			if (normalizedFilters.clusters?.length > 0) {
				const clusterSet = new Set(normalizedFilters.clusters.map(Number));
				if (!dl.clusterId || !clusterSet.has(Number(dl.clusterId))) {
					return false;
				}
			}

			// Filter by specific clusterId if set
			if (normalizedFilters.clusterId !== null && normalizedFilters.clusterId !== undefined) {
				if (dl.clusterId == null || Number(dl.clusterId) !== Number(normalizedFilters.clusterId)) {
					return false;
				}
			} else if (normalizedFilters.requiresCluster && !dl.clusterId) {
				return false;
			}

			// Filter by delivery location field conditions
			if (normalizedFilters.conditionDeliveryLocation?.length > 0) {
				const deliveryLocationForConditions = deliveryLocationAttributesById ? { ...dl, attributes: deliveryLocationAttributesById.get(Number(dl.id)) || dl.attributes || [] } : dl;
				if (!this._evaluateFieldConditions(deliveryLocationForConditions, normalizedFilters.conditionDeliveryLocation)) return false;
			}

			// Filter to specific delivery location IDs (e.g. relevé context)
			if (normalizedFilters.deliveryLocationIds?.length > 0) {
				const dlSet = new Set(normalizedFilters.deliveryLocationIds.map(Number));
				if (!dlSet.has(Number(dl.id))) return false;
			}

			return true;
		});
	}

	/**
	 * Calculates KPI data
	 * @private
	 */
	async _calculateKpiData(kpi, deliveryLocations, filters, originalKpi = kpi) {
		try {
			const normalizedFilters = this._normalizeFilters(filters);
			const deliveryLocationIds = deliveryLocations.map((dl) => dl.id);

			if (deliveryLocationIds.length === 0) {
				return {
					value: 0,
					percentage: 0,
					difference: 0,
					classColor: '',
					color: 'primary',
					objective: null,
					hasRealObjective: false,
				};
			}

			// Fetch 2 latest appointments per DL: current + previous (V-1) for delta comparison
			const rawAppointments = await this._getLatestAppointments(deliveryLocationIds, 2, normalizedFilters.localAppointments);
			const { currentAppointments, previousAppointments: splitPreviousAppointments } = this._splitCurrentAndPrevious(rawAppointments);

			// When localAppointments is provided (statement in-progress), there is only one appointment per DL.
			// The in-progress visit is usually already persisted (created in an earlier step), so fetching
			// limit:1 returns that same visit and yields diff=0. Fetch limit:2 and exclude any candidate
			// whose uid (or startDate as fallback) matches the in-progress one to get a real V-1.
			let previousAppointments = splitPreviousAppointments;
			if (normalizedFilters.localAppointments !== null && splitPreviousAppointments.length === 0) {
				try {
					const candidates = await getLatestAppointmentsWithStatements(deliveryLocationIds, { limit: 2 });
					const localApts = Array.isArray(normalizedFilters.localAppointments) ? normalizedFilters.localAppointments : [normalizedFilters.localAppointments];
					const localUidByDl = new Map(localApts.map((a) => [a.deliveryLocationId, this._getAppointmentUid(a)]));

					const byDl = new Map();
					candidates.forEach((c) => {
						if (!byDl.has(c.deliveryLocationId)) byDl.set(c.deliveryLocationId, []);
						byDl.get(c.deliveryLocationId).push(c);
					});

					previousAppointments = [];
					byDl.forEach((apts, dlId) => {
						apts.sort((a, b) => new Date(b.startDate || b.createdDate) - new Date(a.startDate || a.createdDate));
						const localUid = localUidByDl.get(dlId);
						const v1 = apts.find((a, idx) => {
							const candidateUid = this._getAppointmentUid(a);
							// Definitive uid-based exclusion when both uids are known.
							if (localUid && candidateUid) return candidateUid !== localUid;
							// Fallback: in step 3 the in-progress visit was created in an earlier step,
							// so it is always the most recent saved entry. Skip apts[0].
							return idx > 0;
						});
						if (v1) previousAppointments.push(v1);
					});
				} catch (e) {
					log('warn', 'KpiService', `Could not fetch previous visits for V-1: ${e.message}`);
				}
			}

			const { deliveryLocations: filteredDeliveryLocations, latestAppointments } = this._filterByCompanies(deliveryLocations, currentAppointments, normalizedFilters.companies);
			const visitedDlIdSet = new Set(latestAppointments.map((a) => a.deliveryLocationId));
			const allParkFilteredDlIds = normalizedFilters.allPark ? filteredDeliveryLocations.map((dl) => dl.id) : filteredDeliveryLocations.map((dl) => dl.id).filter((id) => visitedDlIdSet.has(id));

			// Sum KPIs (count-based) must not include unvisited DLs in réel+allPark mode — allPark has no
			// meaning for them there (no real picking/OOS data for a store that wasn't visited). In simulated
			// mode they behave like every other KPI: unvisited DLs get synthesized assortment data too (gated
			// by DLCategorySegment, same as the grid), and visited DLs are rebuilt from the current assortment
			// — the "simulated" toggle must actually affect sum KPIs, not just the grid.
			// TotalMissingProduct is the one exception: an unvisited store's entire référencement is "sans
			// statut" and counts as manquant in full (see _buildKpiMetrics), so unlike the other sum KPIs it
			// DOES need réel+allPark dilution — excluding unvisited DLs would drop that count to 0.
			const isSumKpi = enumKPI.kpiToSum.includes(kpi.reference);
			const isMissingProductKpi = kpi.reference === 'TotalMissingProduct';

			// Inject fake appointments for DLs without visits (simulatedMode or allPark real mode)
			if (kpi.reference == 'ShareOfShelf') normalizedFilters.simulatedMode = true;
			const effectiveSimulatedMode = normalizedFilters.simulatedMode ?? false;
			const isAllParkRealMode = (!isSumKpi || isMissingProductKpi) && (normalizedFilters.allPark ?? false) && !effectiveSimulatedMode;
			const dlIdsForComputation = isSumKpi && !isMissingProductKpi && !effectiveSimulatedMode ? allParkFilteredDlIds.filter((id) => visitedDlIdSet.has(id)) : allParkFilteredDlIds;
			const { appointments: computationAppointments, activeDlIds } = await this._injectFakeAppointments(latestAppointments, dlIdsForComputation, kpi.reference, effectiveSimulatedMode, isAllParkRealMode);

			if (!computationAppointments || computationAppointments.length === 0) {
				return {
					value: 0,
					percentage: 0,
					difference: 0,
					classColor: '',
					color: 'primary',
					objective: null,
					hasRealObjective: false,
				};
			}

			// Calculate KPI value based on type
			let value = this._calculateKpiValue(kpi.reference, computationAppointments, activeDlIds, normalizedFilters);

			// Calculate objective (business target). Mirrors the grid: only real configured objectives count;
			// hasRealObjective drives whether the gauge shows "Obj" and its colour at all.
			const { objective, hasRealObjective } = this._calculateObjective(kpi, activeDlIds, computationAppointments, originalKpi);

			const isInverted = this._isInvertedKpi(kpi.reference);
			// No configured objective → neutral colour (don't fabricate a "target beaten" green).
			const { classColor, color } = hasRealObjective ? this.defineColors(value, objective, isInverted, kpi.tolerance ?? 0) : { classColor: '', color: 'primary' };

			// Delta = V-1 comparison (previous visit); only fall back to the objective delta when a real objective exists.
			const previousValue = previousAppointments.length > 0 ? this._calculateKpiValue(kpi.reference, previousAppointments, activeDlIds, normalizedFilters) : null;
			const difference = previousValue !== null ? value - previousValue : hasRealObjective ? value - objective : 0;

			return {
				value: parseFloat(value.toFixed(2)),
				percentage: parseFloat(value.toFixed(1)),
				difference: difference > 0 ? `+${difference.toFixed(1)}` : difference.toFixed(1),
				classColor,
				color,
				objective: hasRealObjective ? parseFloat(objective.toFixed(2)) : null,
				hasRealObjective,
			};
		} catch (error) {
			log('error', 'KpiService', `Error calculating KPI: ${error.message}`);
			throw error;
		}
	}

	/**
	 * Builds the computation appointments list for KPI calculation.
	 * In simulated mode: returns a NEW array of rebuilt appointments (one per DL with segment config),
	 *   each carrying product state/facing/width/multiFacings over from the live appointment or LATEST_ snapshot.
	 *   Mirrors BO ProductStatementAnalyze where the assortment is the master list. Does NOT mutate the
	 *   input array, so the visit's live (reactive) appointment objects remain intact for incremental UI updates.
	 * In normal mode: returns the input array as-is and excludes unvisited DLs from the active list.
	 * @private
	 * @returns {{ appointments: Array, activeDlIds: Array }}
	 */
	/**
	 * Rebuilds a DL's statements from its CURRENT assortment, the way simulated computation requires:
	 * every referenced product seeds the denominator (source=0), carrying over its surveyed state/facing
	 * from the live statement when available. A product that is assorted today but was never surveyed is
	 * left state=0 (missing by default) so it still counts at référencement — this is what makes the simulé
	 * denominator the full assortment, not just the products present in the historical statement. Live
	 * products no longer in the assortment are appended as pickings (source=1) so they never inflate the
	 * denominator. Used for both unvisited DLs and visited DLs in simulated mode so they behave identically.
	 * @private
	 */
	_buildAssortmentStatements(deliveryLocationId, accountId, baseStatements, kpiReference, productTrees, segments, productsAssort, { flagSimulated, flagReferencementOnly = false }) {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		const statements = [];
		const isSourceSensitive = kpiReference === 'ShareOfShelf' || kpiReference === 'NumericDistribution' || kpiReference === 'TotalPicking' || kpiReference === 'TotalCode';

		productTrees.forEach((productTree) => {
			const referencement = sfaHelper.getAssortment(deliveryLocationId, productTree.id, accountId, {
				segments: segments,
				productsAssort: productsAssort,
			});

			const existingStatementForTree = baseStatements?.find((s) => s.productTreeId === productTree.id);
			const simulatedStatement = {
				productTreeId: productTree.id,
				products: [],
				size: Number(existingStatementForTree?.size || 0),
			};

			const referencedProductIds = new Set();
			const simulatedProducts = (referencement || []).map((product) => {
				const specificitiesAttribute = product.attributes?.find((attribute) => attribute.id === 69)?.value;
				const lastProduct = existingStatementForTree?.products?.find((p) => p.productId === product.id);

				if (isSourceSensitive) referencedProductIds.add(product.id);

				return {
					productId: product.id,
					productTreeId: product.productTreeId,
					source: 0,
					state: lastProduct?.state ?? 0,
					assortType: product.assortType,
					specificities: specificitiesAttribute ? specificitiesAttribute.split(',').map((value) => parseInt(value)) : [],
					facing: lastProduct?.facing ?? 0,
					width: lastProduct?.width ?? Number(product.width || 0),
					multiFacings: lastProduct?.multiFacings ?? [],
					attributes: lastProduct?.attributes ?? [],
					...(flagSimulated ? { _simulated: true } : {}),
					// Unvisited DLs (tout-inclure / simulé) were never surveyed: ATT RELEVE (conditionShelfStatement)
					// cannot apply to their référencement — it describes a visit that didn't happen. Tag the seeded
					// products so the custom-KPI numerator match keeps them in the denominator on ATT PRODUIT alone.
					...(flagReferencementOnly ? { _referencementOnly: true } : {}),
				};
			});
			simulatedStatement.products.push(...simulatedProducts);

			// Append live products not in the assortment (e.g. picking / source != 0).
			// ShareOfShelf: pickings take up shelf space. NumericDistribution: pickings surveyed in this
			// statement contribute to the DN numerator (can push DN above 100%), same as real mode.
			// Products not in the current assortment are treated as pickings (source=1) regardless of
			// their stored source, so they never inflate the denominator (which counts only source=0).
			if (existingStatementForTree?.products && isSourceSensitive) {
				existingStatementForTree.products.forEach((liveProduct) => {
					if (referencedProductIds.has(liveProduct.productId)) return;
					const source = liveProduct.source === 0 ? 1 : liveProduct.source;
					simulatedStatement.products.push({ ...liveProduct, source, _simulated: true });
				});
			}
			statements.push(simulatedStatement);
		});

		return statements;
	}

	async _injectFakeAppointments(latestAppointments, deliveryLocationIds, kpiReference, simulatedMode = false, allParkRealMode = false) {
		try {
			const appointmentByDl = new Map(latestAppointments.map((appointment) => [appointment.deliveryLocationId, appointment]));

			// Standard real mode: only visited DLs, no fake data needed
			if (!simulatedMode && !allParkRealMode) {
				return {
					appointments: latestAppointments,
					activeDlIds: deliveryLocationIds.filter((id) => appointmentByDl.has(id)),
				};
			}

			const sfaHelper = iframeDataBridge.getSfaHelper();
			const win = iframeDataBridge.getIframeWindow();
			const ecosVM = win?.externis?.ecosVM;

			if (!ecosVM || !sfaHelper) {
				// Fallback for allPark real mode: revert to visited-only (no dilution possible without assortment)
				if (allParkRealMode && !simulatedMode) {
					return {
						appointments: latestAppointments,
						activeDlIds: deliveryLocationIds.filter((id) => appointmentByDl.has(id)),
					};
				}
				return { appointments: latestAppointments, activeDlIds: deliveryLocationIds };
			}

			const productTrees = sfaHelper.getPOSProductTrees(true);

			let segments = [];
			try {
				segments = (await iframeDataBridge.getStorageAsync('SEGMENTS')) || [];
			} catch (e) {
				log('warn', 'KpiService', 'Could not load segments for fake appointments');
			}

			const productsAssort = ecosVM.productsAssort;
			const activeDeliveryLocationIds = [];
			const computationAppointments = [];

			for (const deliveryLocationId of deliveryLocationIds) {
				const settingSegmentExists = ecosVM.DLCategorySegment?.find((segment) => segment.deliveryLocationId === deliveryLocationId) != null;
				const existingAppointment = appointmentByDl.get(deliveryLocationId);

				// Keep real appointment for visited DLs in both allPark real mode and simulated mode.
				// In simulated mode, always rebuild from the current assortment so the denominator
				// reflects today's référencement rather than the historical one at the time of the visit.
				// This applies to ALL ratio KPIs (DetentionRate, OOSRate, ShareOfShelf, NumericDistribution):
				// source=0 products that are no longer assorted are reclassified as source=1 (pickings)
				// and products now assorted but never surveyed are seeded with state=0 (absent).
				if (existingAppointment && (allParkRealMode || simulatedMode)) {
					if (simulatedMode && !allParkRealMode) {
						// Rebuild the visited DL's statements from the current assortment, exactly like an unvisited
						// simulated DL: the denominator becomes the full référencement (incl. assorted-but-unsurveyed
						// products counted as missing), not just the products present in the historical statement.
						const dlAccountId = ecosVM.deliveryLocations?.find((dl) => dl.id === deliveryLocationId)?.accountId || 0;
						const baseStatements = existingAppointment._statements || existingAppointment.statements || [];
						const rebuiltStatements = this._buildAssortmentStatements(deliveryLocationId, dlAccountId, baseStatements, kpiReference, productTrees, segments, productsAssort, { flagSimulated: true });
						computationAppointments.push({ ...existingAppointment, _statements: rebuiltStatements });
					} else {
						computationAppointments.push(existingAppointment);
					}
					activeDeliveryLocationIds.push(deliveryLocationId);
					continue;
				}

				// In allPark real mode, include ALL unvisited DLs regardless of DLCategorySegment —
				// their assortment must contribute to the denominator even if no segment is configured.
				if (!settingSegmentExists && !allParkRealMode) {
					continue;
				}

				const accountId = ecosVM.deliveryLocations?.find((deliveryLocation) => deliveryLocation.id === deliveryLocationId)?.accountId || 0;

				// Unvisited DL (no appointment in the period — visited DLs continued above): its référencement
				// is seeded as MISSING (state 0) and contributes to the denominator only. We deliberately do NOT
				// pull the stale LATEST_ survey: a visit that did not happen this period must not turn old
				// surveyed products into present pickings. Otherwise a store whose assortment is now empty would
				// report e.g. 53/0 instead of the expected 0/0 (LECLERC BAR LE DUC).
				const baseStatements = null;

				// allParkRealMode reaches this branch only for unvisited DLs (visited DLs continued above).
				// Their products must not be flagged _simulated or _collectKpiComputationData will strip them.
				const isAllParkUnvisited = allParkRealMode && !simulatedMode;
				const rebuiltStatements = this._buildAssortmentStatements(deliveryLocationId, accountId, baseStatements, kpiReference, productTrees, segments, productsAssort, { flagSimulated: !isAllParkUnvisited, flagReferencementOnly: true });
				const simulatedAppointment = { deliveryLocationId: deliveryLocationId, _statements: rebuiltStatements, _simulated: true, ...(isAllParkUnvisited ? { _isAllParkUnvisited: true } : {}) };

				computationAppointments.push(simulatedAppointment);
				activeDeliveryLocationIds.push(deliveryLocationId);
			}

			return { appointments: computationAppointments, activeDlIds: activeDeliveryLocationIds };
		} catch (error) {
			log('error', 'KpiService', `Error injecting fake appointments: ${error.message}`);
			return { appointments: latestAppointments, activeDlIds: deliveryLocationIds };
		}
	}

	/**
	 * Gets latest appointments with statements.
	 * If localAppointments is provided, uses them directly (e.g. relevé in-progress context).
	 * @private
	 */
	async _getLatestAppointments(deliveryLocationIds, limit = 1, localAppointments = null) {
		if (localAppointments !== null) {
			return Array.isArray(localAppointments) ? localAppointments : [localAppointments];
		}
		try {
			return await getLatestAppointmentsWithStatements(deliveryLocationIds, { limit });
		} catch (error) {
			log('error', 'KpiService', `Error getting appointments: ${error.message}`);
			return [];
		}
	}

	/**
	 * Splits a flat array of appointments (up to N per DL) into current (latest) and previous (V-1) per DL.
	 * Assumes the API returns appointments sorted newest-first per DL.
	 * @private
	 */
	_splitCurrentAndPrevious(appointments) {
		const byDl = new Map();
		appointments.forEach((apt) => {
			if (!byDl.has(apt.deliveryLocationId)) byDl.set(apt.deliveryLocationId, []);
			byDl.get(apt.deliveryLocationId).push(apt);
		});

		byDl.forEach((apts) => {
			apts.sort((a, b) => new Date(b.startDate || b.createdDate) - new Date(a.startDate || a.createdDate));
		});

		const currentAppointments = [];
		const previousAppointments = [];
		byDl.forEach((apts) => {
			if (apts[0]) currentAppointments.push(apts[0]);
			if (apts[1]) previousAppointments.push(apts[1]);
		});

		return { currentAppointments, previousAppointments };
	}

	/**
	 * Collects the raw products and shelf size used to calculate a KPI.
	 * @private
	 */
	_collectKpiComputationData(appointments, deliveryLocationIds, filters = {}) {
		const normalizedFilters = this._normalizeFilters(filters);
		let products = [];
		let shelvesSize = 0;

		const filteredAppointments = appointments.filter((appointment) => deliveryLocationIds.includes(appointment.deliveryLocationId));

		filteredAppointments.forEach((appointment) => {
			const rawStatements = appointment._statements ?? appointment.statements;
			if (!rawStatements) return;

			const statements = this._getFilteredStatements(rawStatements, normalizedFilters);

			statements.forEach((statement) => {
				shelvesSize += Number(statement.size || 0);
				const statementPtId = statement.productTreeId;
				// shelfStatementProductModel instances are non-extensible; spread-copy them to add
				// _statementProductTreeId so _buildMatchingProductIdSet can check pickings' surveyed category.
				const filtered = this._getFilteredStatementProducts(statement.products || [], normalizedFilters);
				products.push(...filtered.map((p) => ({ ...p, _statementProductTreeId: statementPtId })));
			});
		});

		if (!normalizedFilters.simulatedMode) {
			products = products.filter((product) => !product._simulated);
		}

		// Build after collecting so pickings' _statementProductTreeId is available for productTree-scoped conditions.
		const matchingProductIdSet = this._buildMatchingProductIdSet(normalizedFilters.conditionProduct, products);
		const numeratorMatchSet = this._buildNumeratorMatchSet(products, matchingProductIdSet, normalizedFilters.conditionShelfStatement);

		return {
			products,
			shelvesSize,
			numeratorMatchSet,
			matchingProductIdSet,
		};
	}

	_buildMatchingProductIdSet(conditions, collectedProducts = []) {
		if (!conditions?.length) return null;
		const ecosVM = iframeDataBridge.getEcosVM();
		const catalogProducts = this._getEcosCollection(ecosVM?.productsAssort);
		if (!catalogProducts.length) {
			return null;
		}

		const matchingIds = new Set();
		catalogProducts.forEach((product) => {
			if (this._evaluateFieldConditions(product, conditions)) {
				matchingIds.add(Number(product.id));
			}
		});

		// Picking products (source 1/2) carry their ORIGIN category's productTreeId in the catalog, but they
		// count in the category they were surveyed in (_statementProductTreeId). Re-evaluate conditions using
		// that surveyed category so a productTree-scoped conditionProduct does not wrongly exclude them.
		collectedProducts.forEach((product) => {
			if ((product.source === 1 || product.source === 2) && product._statementProductTreeId && !matchingIds.has(Number(product.productId))) {
				const catalogProduct = catalogProducts.find((cp) => Number(cp.id) === Number(product.productId));
				const overrideResult = catalogProduct && this._evaluateFieldConditions({ ...catalogProduct, productTreeId: product._statementProductTreeId }, conditions);
				if (overrideResult) {
					matchingIds.add(Number(product.productId));
				}
			}
		});

		return matchingIds;
	}

	/**
	 * Returns the base product list for a statement: the referenced products scoped by specificities.
	 * Custom-KPI attribute conditions are applied later in _buildKpiMetrics to both numerator and
	 * denominator via the numeratorMatchSet. See _buildNumeratorMatchSet / _buildKpiMetrics.
	 * @private
	 */
	_getFilteredStatementProducts(products = [], filters = {}) {
		let filtered = products;

		if (filters.spes?.length > 0) {
			const catalogSpes = this._getCatalogSpecificitiesMap();
			filtered = filtered.filter((product) => {
				const spes = catalogSpes.get(Number(product.productId)) ?? product.specificities ?? [];
				return spes.length > 0 && filters.spes.some((id) => spes.includes(id));
			});
		}

		return filtered;
	}

	/**
	 * Tests whether a collected product satisfies the custom-KPI attribute conditions that define the
	 * numerator/counter: conditionProduct (ATT Produits, matched via the catalog id set) and
	 * conditionShelfStatement (ATT Relevé, evaluated against the statement product itself).
	 * @private
	 */
	_matchesNumeratorConditions(product, matchingProductIdSet, conditionShelfStatement) {
		if (matchingProductIdSet !== null && !matchingProductIdSet.has(Number(product.productId))) return false;
		// Référencement-only products come from unvisited DLs (tout-inclure / simulé): they were never surveyed,
		// so ATT Relevé cannot apply. Keep them in the match set on ATT Produits alone so they count at
		// référencement (denominator). They are state=0, so they never enter the present/OOS numerator.
		if (product._referencementOnly) return true;
		// ATT Relevé (statement) filters only apply when the product has been surveyed. Absent products
		// (state=2) have stale attribute data but are still in scope for the référencement (denominator).
		// Evaluate them against conditionShelfStatement using their last-known attribute values so they
		// remain counted in the denominator while naturally staying out of the numerator (detentionProducts,
		// eligibleFilteredProducts, etc. all filter by state=1/3).
		if (conditionShelfStatement?.length > 0 && !this._evaluateFieldConditions(product, conditionShelfStatement)) return false;
		return true;
	}

	/**
	 * Builds an object-identity Set of the products that pass the numerator conditions, or null when no
	 * such conditions exist (standard KPI → numerator == base). Keyed on the product object reference so
	 * it disambiguates the same productId appearing across statements/DLs without mutating live state.
	 * @private
	 */
	_buildNumeratorMatchSet(products, matchingProductIdSet, conditionShelfStatement) {
		const hasNumeratorConditions = matchingProductIdSet !== null || conditionShelfStatement?.length > 0;
		if (!hasNumeratorConditions) return null;

		const numeratorMatchSet = new Set();
		products.forEach((product) => {
			if (this._matchesNumeratorConditions(product, matchingProductIdSet, conditionShelfStatement)) numeratorMatchSet.add(product);
		});
		return numeratorMatchSet;
	}

	/**
	 * Returns a memoized Map<productId, assortType> built from the product catalog (ecosVM.productsAssort).
	 * Lets the KPI metrics determine competitor status (assortType 2) reliably, independent of possibly-stale
	 * per-statement assortType values. Not cached while the catalog is empty (not yet loaded).
	 * Rebuilds whenever the productsAssort array reference changes (e.g. background sync landing after this
	 * was first built) — the array is otherwise memoized forever and would silently under-count categories
	 * that hadn't synced yet on first access.
	 * @private
	 */
	_getCatalogAssortTypeMap() {
		const ecosVM = iframeDataBridge.getEcosVM();
		const productsAssort = ecosVM?.productsAssort ?? null;
		if (this._catalogAssortTypeMap && this._catalogAssortTypeMapSource === productsAssort) return this._catalogAssortTypeMap;
		const catalog = this._getEcosCollection(productsAssort);
		const map = new Map();
		catalog.forEach((product) => {
			if (product && product.id != null) map.set(Number(product.id), product.assortType);
		});
		if (catalog.length) {
			this._catalogAssortTypeMap = map;
			this._catalogAssortTypeMapSource = productsAssort;
		}
		return map;
	}

	_getCatalogPublishedSet() {
		const ecosVM = iframeDataBridge.getEcosVM();
		const productsAssort = ecosVM?.productsAssort ?? null;
		if (this._catalogPublishedSet && this._catalogPublishedSetSource === productsAssort) return this._catalogPublishedSet;
		const catalog = this._getEcosCollection(productsAssort);
		const set = new Set();
		catalog.forEach((product) => {
			if (product?.id != null && product.published !== false) set.add(Number(product.id));
		});
		if (catalog.length) {
			this._catalogPublishedSet = set;
			this._catalogPublishedSetSource = productsAssort;
		}
		return set;
	}

	_getCatalogSpecificitiesMap() {
		const ecosVM = iframeDataBridge.getEcosVM();
		const productsAssort = ecosVM?.productsAssort ?? null;
		if (this._catalogSpecificitiesMap && this._catalogSpecificitiesMapSource === productsAssort) return this._catalogSpecificitiesMap;
		const catalog = this._getEcosCollection(productsAssort);
		const map = new Map();
		catalog.forEach((product) => {
			if (product?.id == null) return;
			const attr = product.attributes?.find((a) => a.id === 69)?.value;
			const spes = attr
				? attr
						.split(',')
						.map((v) => parseInt(v))
						.filter((v) => !isNaN(v))
				: [];
			map.set(Number(product.id), spes);
		});
		if (catalog.length) {
			this._catalogSpecificitiesMap = map;
			this._catalogSpecificitiesMapSource = productsAssort;
		}
		return map;
	}

	/**
	 * @param {Set|null} numeratorMatchSet - when non-null, attribute conditions are applied to both the
	 *   numerator/counter and the denominator/base. Null → no filtering, numerator == base.
	 * @param {boolean} simulatedMode - selects the competitor-status source (see rule 1 below).
	 * @param {boolean} simulatedMode - selects the competitor-status source (see rule 1 below).
	 */
	_buildKpiMetrics(kpiReference, products = [], shelvesSize = 0, numeratorMatchSet = null, simulatedMode = false, matchingProductIdSet = null) {
		const normalizedReference = (kpiReference || '').toLowerCase();
		const matchesNumerator = (product) => numeratorMatchSet === null || numeratorMatchSet.has(product);
		// Two distinct rules, both mirroring BO ProductStatementAnalyze:
		//  1. Competitor status (assortType 2). In RÉEL mode it is read from the STATEMENT as surveyed (a product
		//     surveyed as normal keeps counting even if since reclassified). In SIMULÉ mode it is read from the
		//     live CATALOG, because simulation reflects today's assortment & rules — a product that has since
		//     become a competitor must be excluded even though it was surveyed as normal (e.g. STAINS product
		//     145: kept in réel → 27/35, excluded in simulé → 26/8).
		//  2. "Codes non publiés / supprimés": products absent from the catalog or flagged published=false
		//     (ecosVM.productsAssort) are excluded from both numerator and denominator in either mode.
		//     Note: non-published products remain in productsAssort with published:false — checking catalog
		//     presence alone is not enough; the published flag must be checked explicitly.
		const catalogAssortTypeById = this._getCatalogAssortTypeMap();
		const catalogPublishedSet = this._getCatalogPublishedSet();
		const resolveAssortType = (product) => {
			if (!simulatedMode) return product.assortType;
			const catalogAssortType = catalogAssortTypeById.get(Number(product.productId));
			return catalogAssortType !== undefined ? catalogAssortType : product.assortType;
		};
		let isPublished = !simulatedMode
			? (product) => catalogAssortTypeById.size === 0 || catalogAssortTypeById.has(Number(product.productId))
			: (product) => {
					if (catalogAssortTypeById.size === 0) return true;
					if (!catalogAssortTypeById.has(Number(product.productId))) return false;
					return catalogPublishedSet.has(Number(product.productId));
				};

		const eligibleProducts = products.filter((product) => resolveAssortType(product) !== 2 && isPublished(product));
		const sourceProducts = eligibleProducts.filter((product) => product.source === 0);
		const codedProducts = eligibleProducts.filter((product) => product.facing > 0);
		const detentionProducts = sourceProducts.filter((product) => product.state === 1 || product.state === 3);
		const oosProducts = sourceProducts.filter((product) => product.state === 3);
		const eligibleFilteredProducts = eligibleProducts.filter(matchesNumerator);
		const sourceFilteredProducts = sourceProducts.filter(matchesNumerator);
		const codedFilteredProducts = codedProducts.filter(matchesNumerator);

		switch (normalizedReference) {
			case 'detentionrate': {
				const rawDenominator = sourceFilteredProducts.length;
				const rawNumerator = detentionProducts.filter(matchesNumerator).length;
				return {
					aggregationMode: 'ratio',
					referenceCount: rawNumerator,
					totalReferencesCount: rawDenominator,
					rawNumerator,
					rawDenominator,
					rawValue: rawDenominator > 0 ? (rawNumerator / rawDenominator) * 100 : 0,
					value: this._getRatioValue(rawNumerator, rawDenominator),
				};
			}

			case 'shareofshelf': {
				const rawNumerator = this._getShareOfShelfFacingSum(eligibleProducts.filter(matchesNumerator));
				const rawDenominator = Number(shelvesSize || 0);
				return {
					aggregationMode: 'ratio',
					referenceCount: rawNumerator,
					totalReferencesCount: rawDenominator,
					rawNumerator,
					rawDenominator,
					rawValue: rawDenominator > 0 ? (rawNumerator / rawDenominator) * 100 : 0,
					value: this._getRatioValue(rawNumerator, rawDenominator),
				};
			}

			case 'oosrate': {
				const rawDenominator = sourceFilteredProducts.length;
				const rawNumerator = oosProducts.filter(matchesNumerator).length;
				return {
					aggregationMode: 'ratio',
					referenceCount: rawNumerator,
					totalReferencesCount: rawDenominator,
					rawNumerator,
					rawDenominator,
					rawValue: rawDenominator > 0 ? (rawNumerator / rawDenominator) * 100 : 0,
					value: this._getRatioValue(rawNumerator, rawDenominator),
				};
			}

			case 'numericdistribution': {
				const rawDenominator = sourceFilteredProducts.length;
				const rawNumerator = eligibleFilteredProducts.filter((product) => product.state === 1 || product.state === 3).length;
				return {
					aggregationMode: 'ratio',
					referenceCount: rawNumerator,
					totalReferencesCount: rawDenominator,
					rawNumerator,
					rawDenominator,
					rawValue: rawDenominator > 0 ? (rawNumerator / rawDenominator) * 100 : 0,
					value: this._getRatioValue(rawNumerator, rawDenominator),
				};
			}

			case 'totalcode': {
				const rawValue = codedFilteredProducts.length;
				// Référencement is the surveyed/current assortment (source=0), same denominator as TotalPicking/TotalMissingProduct/TotalOOSProduct.
				return {
					aggregationMode: 'sum',
					referenceCount: rawValue,
					totalReferencesCount: sourceFilteredProducts.length,
					rawValue,
					value: rawValue,
				};
			}

			case 'totalpicking': {
				const rawValue = codedFilteredProducts.filter((product) => product.source === 1 || product.source === 2).length;
				// Référencement is the surveyed/current assortment (source=0), same denominator as TotalMissingProduct/TotalOOSProduct.
				return {
					aggregationMode: 'sum',
					referenceCount: rawValue,
					totalReferencesCount: sourceFilteredProducts.length,
					rawValue,
					value: rawValue,
				};
			}

			case 'totalmissingproduct': {
				// State 2 (explicit absent) and state 0 (no status chosen — unvisited DL's seeded référencement,
				// or a surveyed line the rep left without a state) both count as manquant, in either mode.
				const rawValue = sourceFilteredProducts.filter((product) => product.state === 0 || product.state === 2).length;
				return {
					aggregationMode: 'sum',
					referenceCount: rawValue,
					totalReferencesCount: sourceFilteredProducts.length,
					rawValue,
					value: rawValue,
				};
			}

			case 'totaloosproduct': {
				const rawValue = oosProducts.filter(matchesNumerator).length;
				return {
					aggregationMode: 'sum',
					referenceCount: rawValue,
					totalReferencesCount: sourceFilteredProducts.length,
					rawValue,
					value: rawValue,
				};
			}

			default:
				return {
					aggregationMode: 'sum',
					referenceCount: 0,
					totalReferencesCount: 0,
					rawValue: 0,
					value: 0,
				};
		}
	}

	_getRatioValue(numerator, denominator) {
		if (!denominator) {
			return 0;
		}

		return this._roundMetricValue((Number(numerator || 0) / Number(denominator || 0)) * 100);
	}

	_getShareOfShelfFacingSum(products = []) {
		return products.reduce((facingSum, product) => {
			if (product.assortType === 2) {
				return facingSum;
			}

			const defaultFacing = Number(product.facing || 0) * Number(product.width || 0);
			const multiFacing = Array.isArray(product.multiFacings)
				? product.multiFacings.reduce((sum, multiFacingItem) => {
						return sum + Number(multiFacingItem.quantity || 0) * Number(multiFacingItem.furnishingSize || 0);
					}, 0)
				: 0;

			return facingSum + defaultFacing + multiFacing;
		}, 0);
	}

	_roundMetricValue(value) {
		const numericValue = Number(value);
		return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : 0;
	}

	/**
	 * Calculates KPI value based on type and appointments data
	 * @private
	 */
	_calculateKpiValue(kpiReference, appointments, deliveryLocationIds, filters = {}) {
		try {
			const { products, shelvesSize, numeratorMatchSet, matchingProductIdSet } = this._collectKpiComputationData(appointments, deliveryLocationIds, filters);
			const metrics = this._buildKpiMetrics(kpiReference, products, shelvesSize, numeratorMatchSet, this._normalizeFilters(filters).simulatedMode ?? false, matchingProductIdSet);
			return metrics.value;
		} catch (error) {
			log('error', 'KpiService', `Error calculating KPI value: ${error.message}`);
			return 0;
		}
	}

	_getFilteredProductTreeIds(statements = [], filters = {}) {
		const normalizedFilters = this._normalizeFilters(filters);
		const productTreeIds = [...new Set((statements || []).map((statement) => statement.productTreeId).filter((id) => id !== null && id !== undefined))];

		if (!normalizedFilters.pts || normalizedFilters.pts.length === 0) {
			return productTreeIds;
		}

		return productTreeIds.filter((productTreeId) => normalizedFilters.pts.includes(productTreeId));
	}

	_getFilteredStatements(statements = [], filters = {}) {
		const normalizedFilters = this._normalizeFilters(filters);

		return (Array.isArray(statements) ? statements : []).filter((statement) => {
			if (normalizedFilters.pts?.length && !normalizedFilters.pts.includes(statement.productTreeId)) return false;
			return true;
		});
	}

	_evaluateFieldConditions(entity, conditions = []) {
		if (!conditions?.length) return true;
		let result = this._evalCondition(entity, conditions[0]);
		for (let i = 1; i < conditions.length; i++) {
			const next = this._evalCondition(entity, conditions[i]);
			result = conditions[i - 1].logicalOperator === 2 ? result || next : result && next;
		}
		return result;
	}

	/**
	 * Returns a memoized Map<optionId, label> for a list/dropdown attribute, built from
	 * ecosVM.attributes[].values. Returns null for attributes without an option list (free-text,
	 * numeric, boolean…), so their stored value is compared as-is.
	 * @private
	 */
	_getAttributeOptionLabelMap(attributeId) {
		if (!this._attributeOptionLabelCache) this._attributeOptionLabelCache = new Map();
		const cacheKey = Number(attributeId);
		if (this._attributeOptionLabelCache.has(cacheKey)) return this._attributeOptionLabelCache.get(cacheKey);

		const ecosVM = iframeDataBridge.getEcosVM();
		const attribute = this._getEcosCollection(ecosVM?.attributes).find((a) => Number(a?.id) === cacheKey);
		const values = Array.isArray(attribute?.values) ? attribute.values : [];
		const map = values.length ? new Map(values.map((option) => [String(option.id), option.value ?? option.label ?? String(option.id)])) : null;

		this._attributeOptionLabelCache.set(cacheKey, map);
		return map;
	}

	/**
	 * Converts an attribute's stored value (option id, or comma-separated ids for multi-select)
	 * to its label(s). Falls back to the raw value when the attribute has no option list. For a
	 * list-type attribute, a token that matches no known option id is stale/corrupted data (e.g. a
	 * label string saved where an id was expected) rather than a legitimate value — it is dropped
	 * instead of passed through, so it can't coincidentally string-match a resolved condition label.
	 * @private
	 */
	_resolveAttributeValueToLabel(attributeId, rawValue) {
		if (rawValue === undefined || rawValue === null || rawValue === '') return rawValue;
		const optionMap = this._getAttributeOptionLabelMap(attributeId);
		if (!optionMap) return rawValue;
		return String(rawValue)
			.split(',')
			.map((token) => optionMap.get(token.trim()))
			.filter(Boolean)
			.join(',');
	}

	/**
	 * Resolves a delivery location's effective category segment id for a given product tree, mirroring
	 * sfaHelper.getShelfSegment: exact (dl, pt) assignment first, then the global (productTreeId === 0)
	 * fallback. Returns null when the store has no segment configured for that product tree.
	 * @private
	 */
	_getDeliveryLocationSegmentId(deliveryLocationId, productTreeId) {
		if (deliveryLocationId === null || deliveryLocationId === undefined) return null;
		const ecosVM = iframeDataBridge.getEcosVM();
		const segments = this._getEcosCollection(ecosVM?.DLCategorySegment);
		let entry = segments.find((scs) => Number(scs.deliveryLocationId) === Number(deliveryLocationId) && Number(scs.productTreeId) === Number(productTreeId));
		if (!entry) entry = segments.find((scs) => Number(scs.deliveryLocationId) === Number(deliveryLocationId) && Number(scs.productTreeId) === 0);
		return entry ? Number(entry.segmentId) : null;
	}

	/**
	 * Evaluates a SegmentProductTree condition. SegmentProductTree is a virtual delivery-location property
	 * whose value is one or more "productTreeId|segmentId" tokens (e.g. "326|36") referring to the store's
	 * category-segment assignment (ecosVM.DLCategorySegment), not a direct field. A token matches when the
	 * store's effective segment for that product tree equals the token's segment id.
	 * @private
	 */
	_evalSegmentProductTreeCondition(deliveryLocation, condition) {
		const deliveryLocationId = deliveryLocation?.id;
		const tokenMatches = (token) => {
			const [productTreePart, segmentPart] = String(token).split('|');
			const productTreeId = Number(productTreePart);
			const segmentId = Number(segmentPart);
			if (!Number.isFinite(productTreeId) || !Number.isFinite(segmentId)) return false;
			const effectiveSegmentId = this._getDeliveryLocationSegmentId(deliveryLocationId, productTreeId);
			return effectiveSegmentId !== null && effectiveSegmentId === segmentId;
		};

		const values = String(condition.value ?? '')
			.split(',')
			.map((value) => value.trim())
			.filter(Boolean);
		const hasAnySegment = this._getEcosCollection(iframeDataBridge.getEcosVM()?.DLCategorySegment).some((scs) => Number(scs.deliveryLocationId) === Number(deliveryLocationId));

		switch (condition.operator) {
			case 2: // NotEqual
				return !values.some(tokenMatches);
			case 11: // Empty (no segment configured)
				return !hasAnySegment;
			case 12: // NotEmpty (has a segment configured)
				return hasAnySegment;
			case 1: // Equal
			case 17: // InList
			default:
				return values.some(tokenMatches);
		}
	}

	_evalCondition(entity, condition) {
		const propertyName = condition.localizationKey || condition.propertyName || '';
		// SegmentProductTree is a virtual delivery-location property ("productTreeId|segmentId"), resolved
		// against the store's DLCategorySegment assignment rather than a direct field or attribute.
		if (propertyName.toLowerCase() === 'segmentproducttree') {
			return this._evalSegmentProductTreeCondition(entity, condition);
		}
		const keyLower = propertyName ? propertyName.toLowerCase() : '';
		const matchingKey = keyLower ? Object.keys(entity || {}).find((k) => k.toLowerCase() === keyLower) : undefined;
		const directValue = matchingKey !== undefined ? entity[matchingKey] : undefined;
		const rawAttrs = entity.attributes;
		const attrs = Array.isArray(rawAttrs)
			? rawAttrs
			: typeof rawAttrs === 'string'
				? (() => {
						try {
							return JSON.parse(rawAttrs);
						} catch {
							return [];
						}
					})()
				: [];
		let fieldValue;
		let rawAttrValue;
		if (directValue !== undefined) {
			fieldValue = directValue;
		} else {
			rawAttrValue = attrs.length ? attrs.find((a) => Number(a.id) === Number(condition.attributeId))?.value : undefined;
			fieldValue = condition.attributeId ? this._resolveAttributeValueToLabel(condition.attributeId, rawAttrValue) : rawAttrValue;
		}
		const condValue = condition.attributeId ? this._resolveAttributeValueToLabel(condition.attributeId, condition.value) : condition.value;
		switch (condition.operator) {
			case 1:
				return String(fieldValue) === String(condValue);
			case 2:
				return String(fieldValue) !== String(condValue);
			case 3:
				return String(fieldValue ?? '').includes(condValue);
			case 4:
				return !String(fieldValue ?? '').includes(condValue);
			case 5:
				return String(fieldValue ?? '').startsWith(condValue);
			case 6:
				return String(fieldValue ?? '').endsWith(condValue);
			case 7:
				return Number(fieldValue) > Number(condValue);
			case 8:
				return Number(fieldValue) < Number(condValue);
			case 9:
				return Number(fieldValue) >= Number(condValue);
			case 10:
				return Number(fieldValue) <= Number(condValue);
			case 11:
				return fieldValue == null || fieldValue === '';
			case 12:
				return fieldValue != null && fieldValue !== '';
			case 13:
				return new Date(fieldValue) < new Date(condValue);
			case 14:
				return new Date(fieldValue) > new Date(condValue);
			case 15:
				return fieldValue === true;
			case 16:
				return fieldValue === false;
			case 17:
				return String(condValue ?? '')
					.split(',')
					.map((v) => v.trim())
					.includes(String(fieldValue));
			default:
				return true;
		}
	}

	_buildProductTreeLevels(productTreeId, productTreeById, maxLevel = 0) {
		const path = [];
		let currentProductTree = productTreeById.get(productTreeId) || null;

		while (currentProductTree) {
			path.unshift(currentProductTree);
			currentProductTree = currentProductTree.parentId ? productTreeById.get(currentProductTree.parentId) || null : null;
		}

		const productTreeLevels = {};
		path.forEach((productTree, index) => {
			productTreeLevels[`productTreeLevel${index + 1}`] = productTree.label || productTree.name || productTree.reference || '';
		});

		for (let level = path.length + 1; level <= maxLevel; level += 1) {
			productTreeLevels[`productTreeLevel${level}`] = '';
		}

		const leafProductTree = path[path.length - 1] || productTreeById.get(productTreeId) || null;
		return {
			productTree: leafProductTree?.label || leafProductTree?.name || leafProductTree?.reference || '',
			productTreeLevels,
		};
	}

	_getSpecificityLabelMap(attributes = []) {
		const specificityAttribute = this._getEcosCollection(attributes).find((attribute) => Number(attribute?.id) === 69);
		return new Map((Array.isArray(specificityAttribute?.values) ? specificityAttribute.values : []).map((specificity) => [Number(specificity.id), specificity.value || specificity.label || `${specificity.id}`]));
	}

	_getSpecificityIdsFromProducts(products = []) {
		return [
			...new Set(
				(Array.isArray(products) ? products : [])
					.flatMap((product) => (Array.isArray(product?.specificities) ? product.specificities : []))
					.map((specificityId) => Number(specificityId))
					.filter((specificityId) => Number.isInteger(specificityId) && specificityId > 0)
			),
		];
	}

	async _buildKpiDetailRows(kpi, deliveryLocations, latestAppointments, activeDlIds, filters = {}, originalKpi = kpi) {
		const normalizedFilters = this._normalizeFilters(filters);
		const matchingProductIdSet = this._buildMatchingProductIdSet(normalizedFilters.conditionProduct);
		const ecosVM = iframeDataBridge.getEcosVM();
		const sfaHelper = iframeDataBridge.getSfaHelper();
		const accountById = new Map(this._getEcosCollection(ecosVM?.accounts).map((account) => [account.id, account]));
		const channelById = new Map(this._getEcosCollection(ecosVM?.channels).map((channel) => [channel.id, channel]));
		const deliveryLocationTypeById = new Map(this._getEcosCollection(ecosVM?.deliveryLocationTypes).map((deliveryLocationType) => [deliveryLocationType.id, deliveryLocationType]));
		const organisationById = new Map(this._getEcosCollection(ecosVM?.organisations).map((organisation) => [organisation.id, organisation]));
		const productTreeById = new Map(this._getEcosCollection(ecosVM?.productTree).map((productTree) => [productTree.id, productTree]));
		const productTreeMaxLevel = Math.max(...this._getEcosCollection(ecosVM?.productTree).map((productTree) => Number(productTree?.depth || 0) + 1), 0);
		const specificityLabelById = this._getSpecificityLabelMap(ecosVM?.attributes);
		const activeDlIdSet = new Set(activeDlIds);
		const organisationMaxLevel = await getOrganisationMaxLevel();
		const attributeMaps = await buildDeliveryLocationAttributeMaps();
		const organisationSharings = await getOrganisationSharings();
		const orgSharingExpansionMap = this._buildOrgSharingExpansionMap(organisationSharings);
		const context = iframeDataBridge.getContext();
		const userOrgId = Number(context?.user?.organisationId || 0);
		// Custom KPIs carry attribute-based objectives on their own clusterings; resolve them natively
		// (the legacy getGlobalObjective path below receives the PARENT reference and would miss them).
		const isCustomKpi = Number(originalKpi?.kpiId || 0) > 0;
		const customClusterings = isCustomKpi ? originalKpi.clusterings : null;
		const orgIdHierarchy = [...this._getOrgParentIds(this._getEcosCollection(ecosVM?.organisations), userOrgId), 0];
		const userVisibleOrgIds = this._buildUserVisibleOrgIds(userOrgId, organisationById);
		const userOrgStrictScope = userOrgId > 0 && organisationById.has(userOrgId) ? new Set([userOrgId, ...[...organisationById.keys()].filter((id) => this._isAncestorOf(userOrgId, organisationById.get(id), organisationById))]) : null;

		// Pre-compute assortment sizes for unvisited DLs in allPark real mode so
		// that the group row denominator correctly includes non-visited stores.
		const unvisitedTotalRefs = new Map();
		if (normalizedFilters.allPark && !normalizedFilters.simulatedMode && sfaHelper?.getPOSProductTrees && sfaHelper?.getAssortment) {
			const productTrees = sfaHelper.getPOSProductTrees(true) || [];
			let segments = [];
			try {
				segments = (await iframeDataBridge.getStorageAsync('SEGMENTS')) || [];
			} catch (e) {}
			const productsAssort = ecosVM?.productsAssort;
			const visitedDlIds = new Set(latestAppointments.map((apt) => apt.deliveryLocationId));
			for (const dl of deliveryLocations) {
				if (visitedDlIds.has(dl.id)) continue;
				const accountId = ecosVM?.deliveryLocations?.find((d) => d.id === dl.id)?.accountId || 0;
				let totalRefs = 0;
				for (const productTree of productTrees) {
					if (normalizedFilters.pts?.length && !normalizedFilters.pts.includes(productTree.id)) continue;
					const referencement = sfaHelper.getAssortment(dl.id, productTree.id, accountId, { segments, productsAssort }) || [];
					// Carry productId / surveyed category / catalog attributes so the custom KPI's product
					// conditions (ATT PRODUIT) can filter the référencement, just like a visited DL's denominator.
					const mockProducts = referencement.map((p) => {
						const specificitiesAttribute = p.attributes?.find((attribute) => attribute.id === 69)?.value;
						return {
							productId: p.id,
							productTreeId: p.productTreeId,
							_statementProductTreeId: productTree.id,
							source: 0,
							state: 0,
							assortType: p.assortType,
							specificities: specificitiesAttribute ? specificitiesAttribute.split(',').map((value) => parseInt(value)) : [],
							attributes: p.attributes ?? [],
						};
					});
					const filtered = this._getFilteredStatementProducts(mockProducts, normalizedFilters);
					// Apply ATT PRODUIT (catalog conditions) to the référencement denominator. ATT RELEVE
					// (conditionShelfStatement) is intentionally NOT applied: those describe what a visit
					// surveyed, which doesn't exist for an unvisited store — applying them would zero out
					// every unvisited denominator whenever the custom KPI has any statement condition.
					const numeratorMatchSet = matchingProductIdSet !== null ? new Set(filtered.filter((product) => matchingProductIdSet.has(Number(product.productId)))) : null;
					const { totalReferencesCount } = this._buildKpiMetrics(kpi.reference, filtered, 0, numeratorMatchSet, false, matchingProductIdSet);
					totalRefs += totalReferencesCount;
				}
				unvisitedTotalRefs.set(dl.id, totalRefs);
			}
		}

		return deliveryLocations
			.flatMap((deliveryLocation) => {
				const appointment = latestAppointments.find((item) => item.deliveryLocationId === deliveryLocation.id) || null;
				const isActive = activeDlIdSet.has(deliveryLocation.id);
				const account = accountById.get(deliveryLocation.accountId);
				const parentAccount = account?.parentId ? accountById.get(account.parentId) : null;
				const channel = channelById.get(deliveryLocation.channelId);
				const deliveryLocationType = deliveryLocationTypeById.get(deliveryLocation.deliveryLocationTypeId);
				const companyName = deliveryLocation.companyName || deliveryLocation.label || '';
				const deliveryLocationLabel = companyName || deliveryLocation.label;
				const filteredStatements = this._getFilteredStatements(appointment?._statements ?? appointment?.statements, normalizedFilters);

				const deliveryLocationOrganisationIds = Array.isArray(deliveryLocation.organisations) ? deliveryLocation.organisations : [];
				const originalOrgIds = deliveryLocationOrganisationIds.map(Number);
				const orgIdsWithSharing = new Set(originalOrgIds);
				for (const orgId of originalOrgIds) {
					const partners = orgSharingExpansionMap.get(orgId);
					if (partners) {
						for (const partnerId of partners) orgIdsWithSharing.add(partnerId);
					}
				}
				const expandedOrgIds = normalizedFilters.organisations?.length ? this._expandOrganisationIdsWithAncestors(normalizedFilters.organisations, organisationById) : null;
				// Use the store's raw org assignments for the display path so that a store assigned to
				// "Amiens" doesn't also appear under "Paris" when org sharing expands the pool.
				// Fall back to sharing-expanded orgs only when the user can't directly see any of the
				// store's assigned orgs (e.g. a CS1/Paris user viewing an Amiens-assigned store via sharing).
				const userScopedRawOrgIds = userVisibleOrgIds ? originalOrgIds.filter((id) => userVisibleOrgIds.has(id)) : originalOrgIds;
				const hiddenOrgIds = originalOrgIds.filter((id) => !userScopedRawOrgIds.includes(id));
				const sharingVisibleFromHiddenOrgs = new Set();
				for (const orgId of hiddenOrgIds) {
					const partners = orgSharingExpansionMap.get(Number(orgId));
					if (partners) {
						for (const partnerId of partners) {
							if (!userVisibleOrgIds || userVisibleOrgIds.has(partnerId)) sharingVisibleFromHiddenOrgs.add(partnerId);
						}
					}
				}
				const combined = [...new Set([...userScopedRawOrgIds, ...sharingVisibleFromHiddenOrgs])];
				const userScopedOrgIds = combined.length > 0 ? combined : userVisibleOrgIds ? [...orgIdsWithSharing].filter((id) => userVisibleOrgIds.has(id)) : [...orgIdsWithSharing];

				if (userOrgStrictScope && orgIdsWithSharing.size > 0 && ![...orgIdsWithSharing].some((id) => userOrgStrictScope.has(id))) {
					return [];
				}
				const organisationIds = expandedOrgIds ? userScopedOrgIds.filter((id) => expandedOrgIds.has(id)) : userScopedOrgIds;
				const organisations = organisationIds.map((id) => organisationById.get(id)).filter(Boolean);

				const organisationPaths = this._buildOrganisationPaths(organisations, organisationById, organisationMaxLevel);
				const organisationLevels = organisationPaths[0] || {};
				const hasMultipleOrgPaths = organisationPaths.length > 1;

				if (filteredStatements.length === 0 && normalizedFilters.allPark) {
					const totalReferencesCount = unvisitedTotalRefs.get(deliveryLocation.id) ?? 0;
					// An unvisited DL was never surveyed, so every référencé product is "sans statut" — for
					// TotalMissingProduct that counts as manquant in full, same as a visited line left without a state.
					const isMissingProductKpi = (kpi.reference || '').toLowerCase() === 'totalmissingproduct';
					const rawValue = isMissingProductKpi ? totalReferencesCount : 0;
					const { classColor, color } = this.defineColors(rawValue, rawValue, this._isInvertedKpi(kpi.reference), kpi.tolerance ?? 0);
					const { aggregationMode } = this._buildKpiMetrics(kpi.reference, [], 0);
					return [
						{
							id: `${deliveryLocation.id}-unvisited`,
							deliveryLocationId: deliveryLocation.id,
							productTreeId: null,
							reference: deliveryLocation.reference || deliveryLocation.customerCode || deliveryLocation.code || '',
							companyName,
							deliveryLocation: deliveryLocationLabel,
							parentAccount: parentAccount?.name || parentAccount?.label || parentAccount?.reference || '',
							account: account?.name || account?.label || account?.reference || '',
							channel: channel?.name || channel?.label || channel?.reference || '',
							deliveryLocationType: deliveryLocationType?.name || deliveryLocationType?.label || deliveryLocationType?.reference || '',
							address: [deliveryLocation.addressLine, deliveryLocation.addressLine2].filter(Boolean).join(' '),
							postcode: deliveryLocation.postcode || deliveryLocation.postalCode || '',
							city: deliveryLocation.city || '',
							productTree: '',
							specificities: '',
							...resolveDeliveryLocationAttributes(deliveryLocation, attributeMaps),
							...organisationLevels,
							_organisationPaths: hasMultipleOrgPaths ? organisationPaths : null,
							value: rawValue,
							objective: rawValue,
							difference: 0,
							// isActive drives KpiBuilder.vue's getKpiDetailGroupRowAgg: it only sums `value`/
							// `referenceCount` over active rows when grouping (by org, account, etc). For every
							// other sum KPI an unvisited DL contributes 0 so isActive:false is correct, but for
							// TotalMissingProduct the row's value IS the full référencement (see rawValue above)
							// and must be included in grouped sums too, or a grouped view undercounts manquants
							// vs the ungrouped circle total.
							isActive: isMissingProductKpi,
							aggregationMode,
							referenceCount: rawValue,
							totalReferencesCount,
							rawValue,
							rawNumerator: rawValue,
							rawDenominator: totalReferencesCount,
							classColor,
							color,
							hasStatement: false,
						},
					];
				}

				return filteredStatements
					.map((statement) => {
						const products = isActive ? this._getFilteredStatementProducts(statement?.products || [], normalizedFilters) : [];
						if (normalizedFilters.spes && normalizedFilters.spes.length > 0 && products.length === 0) {
							return null;
						}
						const numeratorMatchSet = this._buildNumeratorMatchSet(products, matchingProductIdSet, normalizedFilters.conditionShelfStatement);
						const shelvesSize = isActive ? Number(statement?.size || 0) : 0;
						const metrics = this._buildKpiMetrics(kpi.reference, products, shelvesSize, numeratorMatchSet, normalizedFilters.simulatedMode ?? false, matchingProductIdSet);
						const value = isActive ? metrics.value : 0;

						let objective = 0;
						let hasRealObjective = false;

						if (isActive && isCustomKpi) {
							// Match the objective by its own deliveryLocationConditions (account / segment /
							// SegmentProductTree). Mirrors sfaHelper.getGlobalObjective's custom path used in visits.
							const customObjective = this._findCustomKpiObjectiveValue(customClusterings, deliveryLocation, orgIdHierarchy);
							if (customObjective != null) {
								objective = customObjective;
								hasRealObjective = true;
							}
						}

						if (!objective && isActive && !isCustomKpi && sfaHelper?.kpi?.business?.getGlobalObjective) {
							objective = sfaHelper.kpi.business.getGlobalObjective(deliveryLocation.id, kpi.reference, [statement.productTreeId]);
							if (objective) hasRealObjective = true;
						}

						if (!objective && isActive && !isCustomKpi && sfaHelper?.kpi?.business?.getGlobalObjectiveByPt) {
							try {
								objective = sfaHelper.kpi.business.getGlobalObjectiveByPt(kpi, statement.productTreeId, [deliveryLocation.id]);
								if (objective) hasRealObjective = true;
							} catch {
								objective = 0;
							}
						}

						if (!objective) {
							objective = value;
						}

						const { productTree, productTreeLevels } = this._buildProductTreeLevels(statement.productTreeId, productTreeById, productTreeMaxLevel);
						const specificityIds = this._getSpecificityIdsFromProducts(products);
						const specificities = specificityIds.map((specificityId) => specificityLabelById.get(specificityId) || `${specificityId}`).join(', ');
						const roundedValue = Number(value.toFixed(2));
						const roundedObjective = Number(objective.toFixed(2));
						const difference = Number((roundedValue - roundedObjective).toFixed(2));
						const { classColor, color } = this.defineColors(roundedValue, roundedObjective, this._isInvertedKpi(kpi.reference), kpi.tolerance ?? 0);

						return {
							id: `${deliveryLocation.id}-${statement.productTreeId}`,
							deliveryLocationId: deliveryLocation.id,
							productTreeId: statement.productTreeId,
							reference: deliveryLocation.reference || deliveryLocation.customerCode || deliveryLocation.code || '',
							companyName,
							deliveryLocation: deliveryLocationLabel,
							parentAccount: parentAccount?.name || parentAccount?.label || parentAccount?.reference || '',
							account: account?.name || account?.label || account?.reference || '',
							channel: channel?.name || channel?.label || channel?.reference || '',
							deliveryLocationType: deliveryLocationType?.name || deliveryLocationType?.label || deliveryLocationType?.reference || '',
							address: [deliveryLocation.addressLine, deliveryLocation.addressLine2].filter(Boolean).join(' '),
							postcode: deliveryLocation.postcode || deliveryLocation.postalCode || '',
							city: deliveryLocation.city || '',
							productTree,
							specificities,
							...productTreeLevels,
							...resolveDeliveryLocationAttributes(deliveryLocation, attributeMaps),
							...organisationLevels,
							_organisationPaths: hasMultipleOrgPaths ? organisationPaths : null,
							value: roundedValue,
							objective: roundedObjective,
							_hasRealObjective: hasRealObjective,
							difference,
							isActive,
							aggregationMode: metrics.aggregationMode,
							referenceCount: Number(metrics.referenceCount || 0),
							totalReferencesCount: Number(metrics.totalReferencesCount || 0),
							rawValue: Number(metrics.rawValue || 0),
							rawNumerator: Number(metrics.rawNumerator || 0),
							rawDenominator: Number(metrics.rawDenominator || 0),
							classColor,
							color,
							hasStatement: true,
						};
					})
					.filter((row) => row && (row.hasStatement === false || row.productTree || row.specificities || row.referenceCount || row.rawDenominator || row.rawValue || row.objective));
			})
			.sort((firstRow, secondRow) => {
				const deliveryLocationComparison = (firstRow.companyName || firstRow.deliveryLocation || '').localeCompare(secondRow.companyName || secondRow.deliveryLocation || '');
				if (deliveryLocationComparison !== 0) {
					return deliveryLocationComparison;
				}

				const productTreeComparison = (firstRow.productTree || '').localeCompare(secondRow.productTree || '');
				if (productTreeComparison !== 0) {
					return productTreeComparison;
				}

				return (firstRow.city || '').localeCompare(secondRow.city || '');
			});
	}

	/**
	 * Calculates objective value for the KPI:
	 * - per-DL ptList is derived from that DL's statement product trees
	 * - fallback when no objective = the DL's own KPI value
	 * - denominator for average = activeDlIds.length (excludes DLs without segment config)
	 * @private
	 */
	_calculateObjective(kpi, activeDlIds, latestAppointments, originalKpi = kpi) {
		try {
			if (activeDlIds.length === 0) {
				return { objective: this._getDefaultObjective(kpi.reference), hasRealObjective: false };
			}

			const ecosVM = iframeDataBridge.getEcosVM();
			const context = iframeDataBridge.getContext();
			const sfaHelper = iframeDataBridge.getSfaHelper();
			const userOrgaId = context?.user?.organisationId ?? 0;
			const isAvg = enumKPI.kpiToAvg.indexOf(kpi.reference) >= 0;
			const isCustomKpi = originalKpi?.kpiId > 0;
			// Custom kpis carry their own objective config on their own clusterings
			const clusterings = isCustomKpi ? originalKpi.clusterings : kpi.clusterings;
			const deliveryLocationsById = new Map(this._getEcosCollection(ecosVM?.deliveryLocations).map((dl) => [dl.id, dl]));
			const organisations = this._getEcosCollection(ecosVM?.organisations);
			const orgIdHierarchy = [...this._getOrgParentIds(organisations, userOrgaId), 0];

			// Mirror the detail grid (KpiBuilder.getKpiDetailGroupRowAgg): only delivery locations / product trees
			// carrying a REAL configured objective contribute. When none do, hasRealObjective is false so the gauge
			// hides "Obj" instead of fabricating a target from each store's own value.
			let realObjectiveSum = 0;
			let realObjectiveCount = 0;

			activeDlIds.forEach((deliveryLocationId) => {
				const appointment = latestAppointments.find((a) => a.deliveryLocationId === deliveryLocationId);
				// Unvisited DLs in allPark real mode are excluded from the average (same as the grid's inactive rows).
				const isAllParkUnvisited = appointment?._isAllParkUnvisited === true;
				const statements = appointment ? (appointment._statements ?? appointment.statements ?? []) : [];

				if (isAvg && isAllParkUnvisited) return;

				if (!isCustomKpi) {
					// Non-custom KPIs: resolve each PT objective via sfaHelper; count only PTs that have one.
					for (const statement of statements) {
						const ptId = statement.productTreeId;
						let ptObjective = sfaHelper?.kpi?.business?.getGlobalObjective?.(deliveryLocationId, kpi.reference, [ptId]) ?? 0;
						if (!ptObjective && sfaHelper?.kpi?.business?.getGlobalObjectiveByPt) {
							try {
								ptObjective = sfaHelper.kpi.business.getGlobalObjectiveByPt(kpi, ptId, [deliveryLocationId]) ?? 0;
							} catch {
								ptObjective = 0;
							}
						}
						if (ptObjective) {
							realObjectiveSum += ptObjective;
							realObjectiveCount++;
						}
					}
					return;
				}

				// Custom KPIs: objective is selected by the KPI's own clusterings (cluster match or
				// deliveryLocationConditions) then resolved by organisation hierarchy. No fabricated fallback.
				const dl = deliveryLocationsById.get(deliveryLocationId);
				const matchedGlobalValue = this._findCustomKpiObjectiveValue(clusterings, dl, orgIdHierarchy);
				if (matchedGlobalValue) {
					realObjectiveSum += matchedGlobalValue;
					realObjectiveCount++;
				}
			});

			const hasRealObjective = realObjectiveCount > 0;
			const finalObjective = !hasRealObjective ? 0 : isAvg ? realObjectiveSum / realObjectiveCount : realObjectiveSum;
			const roundedObjective = Math.round(finalObjective * 100) / 100;
			return { objective: roundedObjective, hasRealObjective };
		} catch (error) {
			log('error', 'KpiService', `Error calculating objective: ${error.message}`);
			return { objective: this._getDefaultObjective(kpi.reference), hasRealObjective: false };
		}
	}

	_getOrgParentIds(organisations, orgaId) {
		const results = [];
		const visit = (id) => {
			const found = organisations.find((o) => o.id === id);
			if (found) {
				results.push(found.id);
				if (found.parentId) visit(found.parentId);
			}
		};
		visit(orgaId);
		return results;
	}

	_findObjectiveValue(objectives, ptId, orgIdHierarchy) {
		for (const orgId of orgIdHierarchy) {
			const found = objectives.find((o) => o.organisationId === orgId && !o.roleId && o.productTreeId === ptId);
			if (found) return found.value;
		}
		return null;
	}

	/**
	 * Resolves a custom KPI's objective value for a delivery location. Objectives live on the KPI's clusterings;
	 * each applies either as a plain cluster objective (no deliveryLocationConditions => the DL must belong to that
	 * cluster) or as a condition-based group (its deliveryLocationConditions — e.g. AccountId, SegmentProductTree —
	 * must match the DL). Among applicable objectives the value is resolved by organisation hierarchy. Mirrors
	 * sfaHelper.getGlobalObjective's custom path. Returns null when no objective applies.
	 * @private
	 */
	_findCustomKpiObjectiveValue(clusterings, deliveryLocation, orgIdHierarchy) {
		const applicableObjectives = [];
		(clusterings || []).forEach((cluster) => {
			(cluster.objectives || []).forEach((objective) => {
				const dtoConditions = objective.filters?.deliveryLocationConditions || [];
				const applies =
					dtoConditions.length === 0
						? cluster.clusteringId === deliveryLocation?.clusterId
						: this._evaluateFieldConditions(
								deliveryLocation,
								dtoConditions.map((condition) => this._kpiDtoConditionToFcb(condition))
							);
				if (applies) applicableObjectives.push(objective);
			});
		});
		return this._findGlobalObjectiveValue(applicableObjectives, orgIdHierarchy);
	}

	// Resolves an objective value by walking the organisation hierarchy. Callers pass objectives already
	// scoped to the delivery location (e.g. _findCustomKpiObjectiveValue filters by deliveryLocationConditions
	// first), so here we only need the org-hierarchy tie-break.
	_findGlobalObjectiveValue(objectives, orgIdHierarchy) {
		for (const orgId of orgIdHierarchy) {
			const found = objectives.find((o) => o.organisationId === orgId);
			if (found) return found.value;
		}
		return null;
	}

	/**
	 * Returns a Set of product tree IDs the current user can read in the SFA app domain,
	 * or null when no restrictions are configured (all PTs allowed).
	 * Mirrors salesHelper.getRestrictedProductTrees(null, [sfaAppDomainId], 1).
	 * @private
	 */
	async _resolveAllowedProductTreeIds() {
		try {
			const ecosVM = iframeDataBridge.getEcosVM();
			const sfaAppDomain = this._getEcosCollection(ecosVM?.applicationDomains).find((ad) => ad.name === 'SFA');
			if (!sfaAppDomain) return null;

			const restrictions = await getProductTreeRestrictions();
			if (!restrictions?.length) return null;

			const readRestrictions = restrictions.filter((r) => Number(r.applicationDomainId) === Number(sfaAppDomain.id) && r.restrictionType === 1);
			if (!readRestrictions.length) return null;

			const productTrees = this._getEcosCollection(ecosVM?.productTree);
			const getChildIds = (parentId) => {
				const children = productTrees.filter((pt) => Number(pt.parentId) === Number(parentId));
				return children.flatMap((child) => [Number(child.id), ...getChildIds(Number(child.id))]);
			};

			const allowedIds = new Set();
			for (const r of readRestrictions) {
				allowedIds.add(Number(r.id));
				for (const childId of getChildIds(r.id)) allowedIds.add(childId);
			}

			return allowedIds.size > 0 ? allowedIds : null;
		} catch (e) {
			log('warn', 'KpiService', `Could not resolve allowed product tree IDs: ${e.message}`);
			return null;
		}
	}

	/**
	 * Merges the product tree restriction set into the pts/productTrees filter fields.
	 * When allowedProductTreeIds is null, filters are returned unchanged.
	 * @private
	 */
	_applyProductTreeRestrictions(normalizedFilters, allowedProductTreeIds) {
		if (!allowedProductTreeIds) return normalizedFilters;
		const existingPts = normalizedFilters.pts;
		const pts = existingPts?.length ? existingPts.filter((id) => allowedProductTreeIds.has(Number(id))) : [...allowedProductTreeIds];
		return { ...normalizedFilters, pts, productTrees: pts };
	}

	/**
	 * Returns whether a KPI (main or custom) still exists for the given reference.
	 * Used to detect widget settings pointing at a custom KPI removed from the platform.
	 */
	kpiExists(kpiReference) {
		if (!kpiReference) return false;
		return !!this._getKpiDefinition(kpiReference);
	}

	/**
	 * Gets the list of available KPIs for the builder
	 */
	getAvailableKpis(onlyActivated = false) {
		try {
			const ecosVM = iframeDataBridge.getEcosVM();
			let mainKpis = this._getEcosCollection(ecosVM?.kpis);
			let customKpis = this._getEcosCollection(ecosVM?.kpisCustom);

			mainKpis = mainKpis.filter((kpi) => enumKPI.kpiBuilder.includes(kpi.reference));
			const mainKpiIds = new Set(mainKpis.map((kpi) => kpi.id));

			const allKpis = [...mainKpis, ...customKpis.filter((kpi) => mainKpiIds.has(kpi.kpiId))].map((kpi) => ({
				...kpi,
				_uid: kpi.kpiId ? `c${kpi.id}` : kpi.id,
				referenceTranslated: kpi.kpiId ? kpi.reference : t(kpi.reference),
			}));

			return onlyActivated ? allKpis.filter((kpi) => kpi.activated) : allKpis;
		} catch (error) {
			log('error', 'KpiService', `Error retrieving KPIs: ${error.message}`);
			return [];
		}
	}

	/**
	 * Gets default objective based on KPI type
	 * @private
	 */
	_getDefaultObjective(kpiReference) {
		switch (kpiReference.toLowerCase()) {
			case 'detentionrate':
				return 85;
			case 'shareofshelf':
				return 75;
			case 'oosrate':
				return 5;
			case 'totalmissingproduct':
				return 10;
			case 'numericdistribution':
				return 100;
			default:
				return 80;
		}
	}

	/**
	 * Defines CSS class and Vuetify color based on value vs objective, KPI direction and tolerance band.
	 * Mirrors sfaHelper.defineColors 3-state logic:
	 *   pct >= 100              → success (green)
	 *   pct >= 100 - tolerance  → warning (orange)
	 *   pct < 100 - tolerance   → negative (red)
	 * @param {number} value
	 * @param {number} objective
	 * @param {boolean} isInverted - true if lower is better (e.g. OOSRate)
	 * @param {number} tolerance - tolerance % band from kpi.tolerance (default 0)
	 * @returns {{ classColor: string, color: string }}
	 */
	defineColors(value, objective, isInverted, tolerance = 0) {
		let pct;
		if (!isInverted) {
			pct = objective > 0 ? Math.round((value * 100) / objective) : value > 0 ? 100 : 0;
		} else {
			if (objective === 0) pct = 100 - value;
			else pct = Math.round((1 / ((value || 1) / (objective || value || 1))) * 100);
		}
		if (isNaN(pct)) pct = 0;

		if (pct >= 100) return { classColor: 'positive', color: 'success' };
		if (pct >= 100 - tolerance) return { classColor: 'warning', color: 'warning' };
		return { classColor: 'negative', color: 'error' };
	}

	/**
	 * Checks if KPI is inverted (lower is better)
	 * @private
	 */
	_isInvertedKpi(kpiReference) {
		const inverted = ['oosrate', 'totalmissingproduct'];
		return inverted.includes(kpiReference.toLowerCase());
	}

	/**
	 * Returns true if the KPI value should be displayed with a % suffix
	 * @param {string} kpiReference
	 * @returns {boolean}
	 */
	isPercentage(kpiReference) {
		return enumKPI.valueIsPercentage.some((k) => k.toLowerCase() === kpiReference.toLowerCase());
	}
}

export const kpiService = new KpiService();
